/*
    機械コードを選択するリスト
*/
import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { GetProcs } from "../../models/GetProcs";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import InputLabel from "@mui/material/InputLabel";
import "./MultiMachCodeSelect.css";

const ITEM_HEIGHT = 45;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 8.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export type MachCodeSelectProp = {
  // ラベル
  label: string;
  // 工場コード
  plantCode: string;
  // 加工コード
  procCode: string;
  // 選択中の加工コード
  selectMachCode: string[];
  // 加工コードが選択された場合のイベント処理
  handleMachCodeChange: (e: SelectChangeEvent<string[]>) => void;
  // 値を変更した状態の表示か（枠線の色を変更）
  isValueChangeState?: boolean;
};

interface Option {
  code: string;
  name: string;
}

export const MultiMachCodeSelect: React.FC<MachCodeSelectProp> = ({
  label,
  plantCode,
  procCode,
  selectMachCode,
  handleMachCodeChange,
  isValueChangeState = false,
}) => {
  const labelId = 
  "mach-code-list-label-" +
  new Date().getTime().toString(16) +
  Math.floor(1000 * Math.random()).toString(16);
  // このコンポーネントでユニークなID
  const id =
    "mach-code-list-" +
    new Date().getTime().toString(16) +
    Math.floor(1000 * Math.random()).toString(16);
  // 工場・加工・機械マスタデータ
  const plant_proc_mach = useSelector(
    (state: RootState) => state.procs.plantProcMach
  );

  // 機械コード選択リストデータ作成処理
  const createMachCodes = useCallback(() => {
    const procs: GetProcs[] = plant_proc_mach.filter(
      (d) => d.plant_code === plantCode && d.proc_code === procCode
    );
    let filterProcCodes: Option[] = [];
    if (procs.length > 0) {
      filterProcCodes = procs[0].mach.map((d) => {
        return { code: d.mach_op_code, name: d.mach_name };
      });
    }
    return filterProcCodes;
  }, [plant_proc_mach, plantCode, procCode]);
  const machCodes = createMachCodes();

  return (
    <>
      {label !== "" ? (
        <InputLabel htmlFor={id} id={labelId} className="multi-mach-code-label">
          {label}
        </InputLabel>
      ) : null}
      <Select
        id={id}
        sx={{
          // 標準のborderスタイルを非表示にする
          color: "white",
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(228, 219, 233, 0.0)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(228, 219, 233, 0.0)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(228, 219, 233, 0.0)",
          },
          ".MuiSvgIcon-root ": {
            fill: "white !important",
          },
        }}
        style={
          !isValueChangeState
            ? {
                width: "150px",
                height: "45px",
                border: "2px solid #4AB1CC",
                borderRadius: "4px",
              }
            : {
                width: "150px",
                height: "45px",
                border: "2px solid #FF3366",
                borderRadius: "4px",
              }
        }
        labelId={labelId}
        multiple
        value={selectMachCode}
        onChange={handleMachCodeChange}
        renderValue={(selected: string[]) => {
          const selectedValues = selected.map((selectValue) => {
            const filterProcCode = machCodes.find(
              (d) => d.code === selectValue
            );
            return filterProcCode
              ? selectValue + ":" + filterProcCode.name
              : selectValue + ": ?";
          });
          return (
            <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5 }}>
              {selectedValues.map((value) => (
                <Chip key={value} label={value} />
              ))}
            </Box>
          );
        }}
        MenuProps={MenuProps}
      >
        {machCodes.map(({ code, name }) => (
          <MenuItem key={code} value={code}>
            <Checkbox checked={selectMachCode.indexOf(code) > -1} />
            <ListItemText primary={code + ":" + name} />
          </MenuItem>
        ))}
      </Select>
    </>
  );
};
